/**
 * services/web-ui/src/components/audit/FlowchartView.tsx
 *
 * React Flow rendering of the call's workflow with the actual path traced.
 *
 * - Default workflow edges = gray
 * - Visited path = orange highlighted nodes + edges
 * - Skipped required stages = red dashed border
 * - Active stage = pulsing glow (live calls only)
 * - Click a node → fires onSelect with that stage name
 */

import ReactFlow, {
  Background,
  Controls,
  type Node,
  type Edge,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import type { WorkflowDefinition } from "@vault/types";
import { getStageColors, getStageInfo } from "../../lib/stage-descriptions";

// ─── Pre-defined positions for the 14 default stages ─────────────────────────
// Layered vertical layout: top = GREETING, bottom = END

const DEFAULT_POSITIONS: Record<string, { x: number; y: number }> = {
  GREETING:         { x: 200, y: 0   },
  INTRODUCTION:     { x: 200, y: 80  },
  DISCLOSE_AI:      { x: 200, y: 160 },
  CONSENT_REQUEST:  { x: 0,   y: 240 },
  CONSENT_RESPONSE: { x: 0,   y: 320 },
  GATHER_PURPOSE:   { x: 200, y: 320 },
  ESTABLISH_GOAL:   { x: 200, y: 400 },
  FETCH_INFO:       { x: 400, y: 480 },
  NEGOTIATE:        { x: 200, y: 480 },
  READBACK:         { x: 200, y: 560 },
  CONFIRM:          { x: 200, y: 640 },
  COMMIT:           { x: 200, y: 720 },
  FAREWELL:         { x: 200, y: 800 },
  END:              { x: 200, y: 880 },
  // Voicemail workflow stages
  VOICEMAIL_DETECT: { x: 400, y: 80  },
  LEAVE_VOICEMAIL:  { x: 400, y: 160 },
  // Voice-engine runtime macro-states (right-hand column)
  WAITING_ON_HOLD:  { x: 400, y: 240 },
  NAVIGATING_IVR:   { x: 400, y: 320 },
  PASSTHROUGH:      { x: 400, y: 640 },
  WRAP_UP:          { x: 400, y: 800 },
};

function getPosition(stage: string, index: number): { x: number; y: number } {
  return DEFAULT_POSITIONS[stage] ?? { x: 600, y: index * 80 };
}

// ─── Node custom style builder ─────────────────────────────────────────────

function buildNodeStyle(
  stage: string,
  visited: boolean,
  skipped: boolean,
  isActive: boolean,
  selected: boolean,
): React.CSSProperties {
  const colors = getStageColors(stage);

  const border = skipped
    ? "2px dashed #ef4444"
    : selected
      ? "2px solid #f59e0b"
      : visited
        ? `2px solid ${colors.hex}`
        : "1px solid #374151";

  const background = skipped
    ? "rgba(239, 68, 68, 0.08)"
    : visited
      ? `${colors.hex}22`
      : "#1f2937";

  const color = skipped ? "#fca5a5" : visited ? colors.hex : "#6b7280";

  return {
    border,
    background,
    color,
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 11,
    fontWeight: visited ? 600 : 400,
    minWidth: 100,
    textAlign: "center",
    boxShadow: isActive ? `0 0 12px 3px ${colors.hex}88` : undefined,
    transition: "all 0.2s",
  };
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  workflow: WorkflowDefinition | null;
  stagesVisited: string[];
  stagesSkipped: string[];
  currentStage: string | null;
  selection: { stage?: string; ts_start_ms?: number; ts_end_ms?: number } | null;
  onSelect(stage: string): void;
}

// ─── FlowchartView ────────────────────────────────────────────────────────────

export function FlowchartView({
  workflow,
  stagesVisited,
  stagesSkipped,
  currentStage,
  selection,
  onSelect,
}: Props) {
  if (!workflow) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-900/50 rounded-lg border border-gray-800 text-gray-500 text-sm">
        No workflow definition available
      </div>
    );
  }

  const visitedSet = new Set(stagesVisited);
  const skippedSet = new Set(stagesSkipped);
  const selectedStage = selection && "stage" in selection ? selection.stage : null;

  // Build visited path edges set for orange highlighting
  const visitedEdges = new Set<string>();
  for (let i = 0; i < stagesVisited.length - 1; i++) {
    visitedEdges.add(`${stagesVisited[i]}->${stagesVisited[i + 1]}`);
  }

  // ── Build nodes ──────────────────────────────────────────────────────────
  const nodes: Node[] = Object.entries(workflow.stages).map(([stageName], index) => {
    const visited = visitedSet.has(stageName);
    const skipped = skippedSet.has(stageName);
    const isActive = stageName === currentStage;
    const selected = stageName === selectedStage;
    const info = getStageInfo(stageName);

    return {
      id: stageName,
      position: getPosition(stageName, index),
      data: {
        label: (
          <div title={info.description}>
            <div style={{ fontFamily: "monospace", fontSize: 10 }}>{stageName}</div>
          </div>
        ),
      },
      style: buildNodeStyle(stageName, visited, skipped, isActive, selected),
      type: "default",
    };
  });

  // ── Build edges ──────────────────────────────────────────────────────────
  const edges: Edge[] = [];
  for (const [stageName, stageDef] of Object.entries(workflow.stages)) {
    // next[] edges
    if (stageDef.next) {
      for (const nextStage of stageDef.next) {
        const edgeKey = `${stageName}->${nextStage}`;
        const isTraced = visitedEdges.has(edgeKey);
        edges.push({
          id: edgeKey,
          source: stageName,
          target: nextStage,
          animated: isTraced,
          style: {
            stroke: isTraced ? "#f97316" : "#374151",
            strokeWidth: isTraced ? 2 : 1,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: isTraced ? "#f97316" : "#374151",
          },
        });
      }
    }

    // branch edges
    if (stageDef.branches) {
      for (const branch of stageDef.branches) {
        const edgeKey = `${stageName}->${branch.next}:branch`;
        const isTraced = visitedEdges.has(`${stageName}->${branch.next}`);
        const label = branch.condition
          ? `sig: ${branch.condition.signal}`
          : branch.timeout
            ? `timeout ${branch.timeout.duration_s}s`
            : "default";
        edges.push({
          id: edgeKey,
          source: stageName,
          target: branch.next,
          animated: isTraced,
          label,
          labelStyle: { fontSize: 8, fill: "#6b7280" },
          style: {
            stroke: isTraced ? "#f97316" : "#374151",
            strokeWidth: isTraced ? 2 : 1,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: isTraced ? "#f97316" : "#374151",
          },
        });
      }
    }

    // timeout edge
    if (stageDef.timeout) {
      const edgeKey = `${stageName}->${stageDef.timeout.on_timeout}:timeout`;
      const isTraced = visitedEdges.has(`${stageName}->${stageDef.timeout.on_timeout}`);
      edges.push({
        id: edgeKey,
        source: stageName,
        target: stageDef.timeout.on_timeout,
        animated: isTraced,
        label: `${stageDef.timeout.duration_s}s`,
        labelStyle: { fontSize: 8, fill: "#6b7280" },
        style: {
          stroke: isTraced ? "#f97316" : "#6b7280",
          strokeDasharray: "4 3",
          strokeWidth: 1,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: isTraced ? "#f97316" : "#6b7280",
        },
      });
    }
  }

  return (
    <div style={{ height: 500 }} className="rounded-lg border border-gray-800 overflow-hidden bg-gray-950">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        onNodeClick={(_evt, node) => onSelect(node.id)}
        style={{ background: "#030712" }}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#1f2937" gap={20} />
        <Controls showInteractive={false} style={{ background: "#111827", border: "1px solid #374151" }} />
      </ReactFlow>
    </div>
  );
}
